/* <video-facade> — click-to-play embed, no third-party JS until the user asks.

   Replaces the Alpine `ThemeModule_Video` pattern, which wrapped BOTH the poster
   and the iframe in `<template x-if>`. Because <template> content never enters the
   DOM, that markup rendered nothing at all once Alpine stopped loading (theme.min.js
   is only rendered by layout/gift_card.liquid — ds_plan §4a / Part 6), so every
   video on the site was simply absent.

   This inverts it: the poster and its trigger are real server-rendered markup, so
   they show with JS off, and the trigger is an <a> pointing at the real video URL —
   a no-JS visitor still gets to the video. With JS, we intercept and swap in the
   embed inline.

   Expected markup:
     <video-facade data-embed="https://…/embed/ID?autoplay=1" data-title="…">
       <div data-video-media>
         <a data-video-trigger href="https://youtube.com/watch?v=ID"> …poster… </a>
       </div>
     </video-facade>
*/
class VideoFacade extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-video-trigger]');
    if (!this.trigger || !this.dataset.embed) return;

    // Only now does it behave like a button; without JS it stayed a real link.
    this.trigger.setAttribute('role', 'button');
    this.trigger.addEventListener('click', this.activate.bind(this));
  }

  activate(event) {
    event.preventDefault();
    if (this.dataset.active) return;
    this.dataset.active = 'true';

    const media = this.querySelector('[data-video-media]') || this;
    const iframe = document.createElement('iframe');
    iframe.src = this.dataset.embed;
    iframe.className = 'absolute top-0 left-0 right-0 bottom-0 w-full h-full';
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', this.dataset.title || '');
    iframe.setAttribute('loading', 'lazy');

    this.trigger.remove();
    media.appendChild(iframe);

    // Move focus into the player so keyboard users land where they clicked.
    iframe.addEventListener('load', () => iframe.focus({ preventScroll: true }), { once: true });
  }
}

if (!customElements.get('video-facade')) {
  customElements.define('video-facade', VideoFacade);
}
